import type { RaMatrixDefinition, RiskControl, RiskHazard } from '@felix/domain';
import { Button, Checkbox, Select } from '@mantine/core';
import { IconBooks } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LevelBadge } from '../RiskParts.js';
import { localId } from '../view.js';
import classes from './RaMaster.module.css';
import type { RaMasterView } from './types.js';
import { activityOptions, activitySuggestions, suggestionsToContent } from './view.js';

/**
 * Gemi RA formunda "Şirket kütüphanesinden başla" (K2; ADR 0086 §7; soru 8): gemi kendi RA'sını
 * yazar ama şirket kütüphanesinden başlar. Faaliyet seçilince etkin tehlikeleri ve mevcut / ek
 * kontrolleri önerilir; seçilenler formun tehlike ve önlem listesine eklenir. Kalan risk ilk riske
 * eşit başlar (azalma varsayılmaz). Tam iki katman (yayımlı şirket RA'sı) K3'te gelir.
 */
export function LibraryStart({
  master,
  matrix,
  existingHazards,
  onAdd,
}: {
  master: RaMasterView;
  matrix: RaMatrixDefinition;
  /** Formdaki tehlike adları: aynı tehlike ikinci kez önerilmez. */
  existingHazards: readonly string[];
  onAdd: (patch: {
    hazards: RiskHazard[];
    controls: RiskControl[];
    activity: { name: string; scope: string | null };
  }) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.library.${key}`, options);
  const activities = useMemo(() => activityOptions(master, master.vesselGroup ?? null), [master]);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const suggestions = useMemo(
    () => (activityId ? activitySuggestions(master, activityId, matrix) : []),
    [master, activityId, matrix],
  );
  const taken = new Set(existingHazards.map((h) => h.trim().toLowerCase()));
  const available = suggestions.filter((s) => !taken.has(s.hazard.name.trim().toLowerCase()));
  const chosen = available.filter((s) => picked.has(s.link.id));

  return (
    <section className={classes.library} aria-label={k('title')}>
      <div className={classes.libraryHead}>
        <Select
          label={k('activity')}
          description={k('help')}
          placeholder={activities.length > 0 ? k('placeholder') : k('none')}
          disabled={activities.length === 0}
          leftSection={<IconBooks size={16} aria-hidden />}
          searchable
          clearable
          value={activityId}
          data={activities.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))}
          onChange={(value) => {
            setActivityId(value);
            const next = value ? activitySuggestions(master, value, matrix) : [];
            // Varsayılan: bütün öneriler seçili (kullanıcı çıkarır)
            setPicked(new Set(next.map((s) => s.link.id)));
          }}
        />
        <Button
          disabled={chosen.length === 0}
          onClick={() => {
            const activity = master.activities.find((a) => a.id === activityId);
            onAdd({
              ...suggestionsToContent(chosen, localId),
              activity: { name: activity?.name ?? '', scope: activity?.scope ?? null },
            });
            setPicked(new Set());
          }}
        >
          {k('add', { count: chosen.length })}
        </Button>
      </div>
      {activityId ? (
        <>
          <strong>{k('hazards')}</strong>
          <ul className={classes.suggestions}>
            {suggestions.map((s) => {
              const already = taken.has(s.hazard.name.trim().toLowerCase());
              return (
                <li key={s.link.id} className={classes.suggestion}>
                  <Checkbox
                    aria-label={k('select', { hazard: s.hazard.name })}
                    disabled={already}
                    checked={!already && picked.has(s.link.id)}
                    onChange={(event) => {
                      const next = new Set(picked);
                      if (event.currentTarget.checked) next.add(s.link.id);
                      else next.delete(s.link.id);
                      setPicked(next);
                    }}
                  />
                  <span>
                    {s.hazard.name}
                    <span className={classes.sub}>
                      {already
                        ? k('alreadyAdded')
                        : `${s.consequences.join('; ') || '—'} · ${k('controls', {
                            existing: s.existing.length,
                            additional: s.additional.length,
                          })}`}
                    </span>
                  </span>
                  <LevelBadge level={s.initial.level} score={s.initial.score} />
                </li>
              );
            })}
          </ul>
          <p className={classes.hint}>{k('residualNote')}</p>
        </>
      ) : null}
    </section>
  );
}
